import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MdWarningAmber, MdErrorOutline, MdHelpOutline, MdCheck } from 'react-icons/md';
import { UnifiedButton, CancelButton, DeleteButton } from '../UnifiedButton/UnifiedButton';
import * as styles from './confirmDialog.css';

type ConfirmVariant = 'danger' | 'warning' | 'info';

export interface ConfirmOptions {
  message: string;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
}

type ConfirmFn = (options: ConfirmOptions | string) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Hook renvoyant une fonction `confirm()` basée sur une Promise, affichant
 * une modale thématisée cohérente avec le reste de l'application
 * (remplace window.confirm natif).
 */
export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback sûr si le provider n'est pas monté (évite un crash)
    return (options) =>
      Promise.resolve(window.confirm(typeof options === 'string' ? options : options.message));
  }
  return ctx;
};

const VARIANT_ICON: Record<ConfirmVariant, React.ReactNode> = {
  danger: <MdErrorOutline />,
  warning: <MdWarningAmber />,
  info: <MdHelpOutline />,
};

const VARIANT_ICON_CLASS: Record<ConfirmVariant, string> = {
  danger: styles.iconDanger,
  warning: styles.iconWarning,
  info: styles.iconInfo,
};

const DEFAULT_TITLE: Record<ConfirmVariant, string> = {
  danger: 'Confirmer la suppression',
  warning: 'Confirmation requise',
  info: 'Confirmation',
};

export const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    const normalized: ConfirmOptions = typeof opts === 'string' ? { message: opts } : opts;
    setOptions(normalized);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = useCallback((result: boolean) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setOptions(null);
  }, []);

  // Fermeture clavier (Échap = annuler, Entrée = confirmer)
  useEffect(() => {
    if (!options) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [options, close]);

  const variant: ConfirmVariant = options?.variant ?? 'danger';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <div
          className={styles.backdrop}
          onClick={() => close(false)}
          role="presentation"
        >
          <div
            className={styles.dialog}
            role="alertdialog"
            aria-modal="true"
            aria-label={options.title ?? DEFAULT_TITLE[variant]}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.header}>
              <span className={`${styles.iconWrap} ${VARIANT_ICON_CLASS[variant]}`} aria-hidden="true">
                {VARIANT_ICON[variant]}
              </span>
              <h2 className={styles.title}>{options.title ?? DEFAULT_TITLE[variant]}</h2>
            </div>
            <p className={styles.message}>{options.message}</p>
            <div className={styles.actions}>
              <CancelButton onClick={() => close(false)}>
                {options.cancelText ?? 'Annuler'}
              </CancelButton>
              {variant === 'danger' ? (
                <DeleteButton autoCollapse={false} onClick={() => close(true)}>
                  {options.confirmText ?? 'Supprimer'}
                </DeleteButton>
              ) : (
                <UnifiedButton
                  variant={variant === 'info' ? 'primary' : variant}
                  icon={<MdCheck />}
                  autoCollapse={false}
                  onClick={() => close(true)}
                >
                  {options.confirmText ?? 'Confirmer'}
                </UnifiedButton>
              )}
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmDialogProvider;
